import {Component, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {IFormField} from "../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import {TaxReturnsResolver} from "../tax-returns-resolver";
import { cloneDeep } from "lodash-es";
import {ITaxReturn} from "./model/i-tax-return";
import {Subscription} from "rxjs";
import {getActiveWarnings, getClientRulesWarningMsg} from "../get-full-tax-return";
import { ClientNotificationService } from "../../client-notification.service";
import {ConfigStateService} from "../../../app/config/config-state.service";
import {Tenant} from "../../../shared/types/tenant.enum";
import { TenantConfigService } from "../../../shared/services/tenant-config.service";
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslocoService} from "@jsverse/transloco";

declare var $: any;

@Component({
    selector: "app-tax-return",
    templateUrl: "./tax-return.component.html",
    styleUrls: ["./tax-return.component.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: { scope: 'tax-returns', alias: 'taxReturns' },
        }
    ],
    standalone: false
})
export class TaxReturnComponent implements OnInit, OnDestroy {
  @HostBinding('class.hidden-scrollbar') hiddenScrollbar: boolean = true;
  iconsFilter: any; // dropdown input
  globalFilter: boolean = false;
  taxSearch: string; // search input
  forms: {
    label: String; // heading text
    fragment: String; // fragment name for anchor
    columns: {
      1: IFormField[];
      2: IFormField[];
    }[];
  }[] = [];
  questions: IFormField[] = [];
  taxReturn: ITaxReturn;
  @ViewChild('multiSelectDropdown') multiSelectDropdown: ElementRef;
  lastScrollFragment?: string;
  taxReturnSub?: Subscription;
  queryParamsSub?: Subscription;
  showQuestionId: boolean;
  subscriptions: Subscription[] = [];
  uiConfig: TenantConfigService;
  taxReturnAlias: string;

  constructor(private activatedRoute: ActivatedRoute,
              private taxReturnsResolver: TaxReturnsResolver,
              private notificationService: ClientNotificationService,
              private config: ConfigStateService,
              private translocoService: TranslocoService) {
  }

  ngOnInit() {
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
        this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
      })
    );

    this.taxReturnSub = this.taxReturnsResolver.getTaxReturnObs().subscribe((data: ITaxReturn) => {

      this.taxReturn = cloneDeep(data);

      // append total group count to each subcategory
      this.taxReturn.categories.forEach((category) => {
        category.subcategories.forEach((subCategory) => {
          subCategory.subCategoryGroupsCount = subCategory.groups.length;
        });
      });

      this.taxReturn.categories = this.mapQuestionsToSubcategory();
      this.showQuestionId = this.config.tenant !== Tenant.AU;

      this.displayReadOnlyWarningMessage();
      // init filter dropdown
      setTimeout(() => {
        $(this.multiSelectDropdown.nativeElement).dropdown({
          placeholder: "All",
          preserveHTML: false,
        });
      }, 0);

      setTimeout(() => {
        if (this.queryParamsSub) {
          this.queryParamsSub.unsubscribe();
        }
        this.queryParamsSub = this.activatedRoute.queryParams.subscribe(params => {
          this.scrollToSegment(params.category);
          this.globalFilter = params.isGlobal === "true";
        });
      }, 0);
    });
  }

  ngOnDestroy() {
    if (this.queryParamsSub) this.queryParamsSub.unsubscribe();
    this.taxReturnSub.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // maps a list of top level questions to it's sub category list
  mapQuestionsToSubcategory() {
    return this.taxReturn.categories.map(category => ({
      ...category,
      subcategories: category.subcategories.map(sc => ({
        ...sc,
        questions: sc.questions.filter(q => q.topLevel),
      })),
    }));
  }

  // scroll to subcategory section when navigating from sidebar menu
  scrollToSegment(fragment) {
    if (this.taxReturn && fragment && fragment !== this.lastScrollFragment) {
      const el = $("#" + fragment).get(0);
      if (el) {
        el.scrollIntoView();
        this.lastScrollFragment = fragment;
      }
    }
  }

  displayReadOnlyWarningMessage() {
    if (this.taxReturn.userIsReadOnly) {
      this.notificationService.appendMessage('User Access', 'The form is in read only mode', false);
    }
  }

  getWarningsCount(subCategory) {
    return getActiveWarnings(this.taxReturn, subCategory);
  }

  computeTopLevelAnswer(question, answers) {
    const dataType = question.dataType.toLowerCase();
    if (dataType === "float" || dataType === "integer") {
      const numbers = answers && answers.length && answers.map(a => Number(a.answer)).filter(Number.isFinite) || [];
      return numbers.length ? numbers.reduce((acc, x) => acc + x, 0) : "";
    }
    return answers && answers.length && answers[0].answer || "";
  }

  getClientRulesWarning() {
    return getClientRulesWarningMsg(this.taxReturn.mandatoryIncompleteClientRulesCount);
  }

}
